/**
 * Seat-review gate store (spec 2026-08-03-portal-seat-reconciliation-gate).
 *
 * Portal-local on purpose: the gate is a portal-only concern and keeping it
 * out of packages/shared keeps the guard's blast radius to this app.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api, useAuthStore } from '@claimshield/shared'

export interface SeatReviewDriver {
  employee_id: string
  name: string
  flagged_left: boolean
}

export interface SeatReviewState {
  gated: boolean
  seats?: number
  active_count?: number
  deadline_at?: string | null
  monthly_now?: string
  monthly_if_accept?: string
  drivers?: SeatReviewDriver[]
}

/** Shape of every `{status, data}` envelope the seat-review endpoints return
 * (GET, and POST /remove — which returns the same fresh-state shape plus a
 * `removed` field the interface above doesn't need to declare). */
export interface SeatReviewApiResponse {
  status: string
  data: SeatReviewState | null
}

export const useSeatReviewStore = defineStore('seatReview', () => {
  const loaded = ref(false)
  const gated = ref(false)
  const state = ref<SeatReviewState | null>(null)

  // Identity the cache above is valid for (round-1 review, CRITICAL). A
  // plain `loaded` flag survives an in-SPA logout -> login as a DIFFERENT
  // client (no store remount on a client-side nav) — the previous client's
  // gated state, and driver NAMES (cross-tenant PII), would otherwise render
  // for the new client, unrecoverable without a hard reload. Mirror bug: a
  // genuinely-gated new client would never gate because `loaded` was already
  // true. Keyed on the real logged-in user id — this store only ever runs
  // for `role === 'client'` (the router guard bails before it for staff), so
  // there's no view-as split to key on here the way PortalLayout's own
  // separate seat-review-banner fetch does.
  let loadedForUserId: string | null = null

  // In-flight memoization — same pattern as shared/src/stores/auth.ts's
  // `initializePromise` (~line 130-140): two navigations can race the router
  // guard before `loaded` flips, and without this both fire the GET. That's
  // not just wasted work here — `build_seat_review_state` (the GET handler)
  // auto-resolves a healed requirement server-side, so an un-memoized double
  // fetch is a double-write risk, not merely a double-read one.
  let inFlight: Promise<void> | null = null

  // Identity the CURRENTLY-tracked `inFlight` promise was started for
  // (round-2 review, Important). Round 1 fixed `loaded`/`loadedForUserId`
  // but left `inFlight` itself identity-blind: a GET kicked off for user A
  // that's still in flight got handed straight back to user B's
  // `ensureLoaded()` after a fast identity switch (logout -> login as a
  // different client, no store remount) — when A's response landed it ran
  // through `applyState`, stamping A's `gated`/`state` (INCLUDING DRIVER
  // NAMES — cross-tenant PII) under `loadedForUserId` = B, because
  // `currentUserId()` at *resolve* time was already B. Every write to
  // `state`/`gated`/`loadedForUserId` now funnels through `applyState`
  // below, which is the SOLE place that verifies identity before writing
  // (round-3 review made this structural — see `applyState`'s doc comment
  // for why "identity-checked at the point it actually writes, inside
  // `refresh`" — the claim this comment used to make — was false: a caller
  // reaching the write some other way skipped it entirely).
  let inFlightForUserId: string | null = null

  // The identity every cached answer is keyed on. A client is one user; a
  // staff member viewing as a client walks the same seat review on that
  // client's behalf (TRACK #12, 2026-10-05), and one staff user moves between
  // many clients without a store remount — so view-as is part of the key, or
  // client A's driver names would render while viewing client B.
  function currentUserId(): string | null {
    const auth = useAuthStore()
    const id = auth.user?.id ?? null
    if (!id) return null
    return auth.isViewingAsClient ? `${id}:${auth.viewAsClientId}` : id
  }

  /** Apply a `{status, data}` envelope and stamp the identity it's now valid
   * for — but ONLY if `requestedForUserId` (the identity captured when the
   * underlying fetch/POST was KICKED OFF — never "now") is still the
   * identity logged in when this runs. This is the SOLE choke point for
   * every write to `state`/`gated`/`loadedForUserId`: `refresh()` and
   * `applyRemoveResponse()` both route through here rather than writing
   * directly, so there is no unguarded path left to reintroduce this bug
   * from. On a mismatch nothing is written at all — no state, no
   * `loadedForUserId` stamp — because the payload belongs to whoever it was
   * fetched for, not whoever is current now; stamping `loadedForUserId` to
   * the CURRENT user on a discard would be its own bug (it would convince
   * `ensureLoaded()` the current user's data is already loaded, when what's
   * actually in `state` is the discarded user's). Returns whether it
   * applied, so callers with a follow-up step (the self-heal below) can
   * skip it on a discard.
   *
   * Round-3 review (Important): `applyRemoveResponse` used to call
   * `applyState` with NO identity check at all — a slow `POST /remove` for
   * client A that resolved after an in-SPA logout -> login-as-B landed A's
   * response (including driver NAMES — cross-tenant PII) under
   * `loadedForUserId = B`, and B was then stuck on it: `ensureLoaded()` saw
   * `loadedForUserId === B` and never refetched. Putting the check HERE —
   * the one function that actually assigns `state.value`/`gated.value` —
   * rather than in each caller is what makes it structural. */
  function applyState(
    payload: SeatReviewApiResponse,
    requestedForUserId: string | null,
  ): boolean {
    if (currentUserId() !== requestedForUserId) return false
    state.value = payload.data ?? null
    gated.value = payload.status === 'ok' && !!payload.data?.gated
    loaded.value = true
    loadedForUserId = requestedForUserId
    return true
  }

  /** Fetch fresh state and apply it through `applyState`'s identity guard.
   * Defaults `requestedForUserId` to "now" so a bare `store.refresh()` call
   * (e.g. `SeatReviewView.vue`'s `accept()`) captures the identity at the
   * point `refresh()` itself is invoked — immediately after its preceding
   * `await` resolves, so there's no window for a UI-driven identity switch
   * (a real click, which needs a macrotask) to land in between. The
   * fail-open catch branch isn't a `{status, data}` payload, so it can't
   * route through `applyState` — it re-checks identity directly instead. */
  async function refresh(
    requestedForUserId: string | null = currentUserId(),
  ): Promise<void> {
    try {
      const res = await api.get<SeatReviewApiResponse>('/v1/client/seat-review')
      applyState(res.data, requestedForUserId)
    } catch {
      if (currentUserId() !== requestedForUserId) return
      // Fail open — a 500 must never lock a client out of their compliance data.
      gated.value = false
      loaded.value = true
      loadedForUserId = requestedForUserId
    }
  }

  async function ensureLoaded(): Promise<void> {
    const uid = currentUserId()
    // Reuse the in-flight promise ONLY if it was started for the SAME
    // identity as this call — reusing a promise started for a different
    // user is exactly the bug this fixes (see `inFlightForUserId` above).
    if (inFlight && inFlightForUserId === uid) return inFlight
    if (loaded.value && loadedForUserId === uid) return

    inFlightForUserId = uid
    const promise = refresh(uid).finally(() => {
      // Only clear bookkeeping if it's still tracking THIS fetch — a fast
      // identity switch may already have started a newer fetch (for a
      // different user) that this stale finally() must not stomp on.
      if (inFlightForUserId === uid) {
        inFlight = null
        inFlightForUserId = null
      }
    })
    inFlight = promise
    return promise
  }

  /** Apply POST /remove's response through `applyState`'s identity guard,
   * then self-heal if it's the backend's synthesized degraded shape
   * (round-2 review, minor). When `build_seat_review_state`'s post-commit
   * re-read fails, `client_seat_review.py::remove_drivers` returns only
   * `{gated, resolved, seats, active_count}` — no `drivers`, no
   * `deadline_at`/`monthly_*`. Left as applied, a still-gated client would
   * be stuck on a screen with no driver list to pick from and no way to
   * recover short of a hard reload. `requestedForUserId` is threaded into
   * BOTH the initial `applyState` call and the self-heal `refresh()` — a
   * discarded (identity-mismatched) response must not fire a self-heal GET
   * on the abandoned user's behalf either. One-shot by construction: the
   * healing GET always goes through the plain `refresh()` (not
   * `applyRemoveResponse` again), so if it's ALSO degraded or fails, that
   * GET's own fail-open/render just stands — there's no recursive
   * re-trigger to loop on. Returns whether the initial payload applied, so
   * `submitRemoval` (and the view, through it) can tell a discard from a
   * normal application. */
  async function applyRemoveResponse(
    payload: SeatReviewApiResponse,
    requestedForUserId: string | null,
  ): Promise<boolean> {
    const applied = applyState(payload, requestedForUserId)
    if (applied && gated.value && !(state.value?.drivers && state.value.drivers.length > 0)) {
      await refresh(requestedForUserId)
    }
    return applied
  }

  /** POST /remove and apply its response — the single write path for the
   * remove flow, kept in the STORE (not the view) so `applyState`'s identity
   * guard is unconditionally in the loop. Round-3 review: the prior code had
   * `SeatReviewView.vue` making the `axios` call itself and only handing the
   * response to the store afterward — a structurally weaker arrangement,
   * since nothing forced that hand-off to happen through a guarded path (and
   * in fact it didn't: `applyRemoveResponse` was called with no identity
   * check at all). Moving the POST itself in here means there is no code
   * path outside this file that can apply a `/remove` response.
   *
   * Captures `currentUserId()` at POST-INITIATION — before the `await`, i.e.
   * at request start — so a slow POST straddling a logout -> login-as-a-
   * different-client is attributed to whoever asked for it, never whoever
   * is current when it lands. Network/HTTP failures propagate to the caller
   * unchanged (not caught here) so `SeatReviewView.vue`'s existing
   * try/catch still drives its error banner exactly as before. */
  async function submitRemoval(employeeIds: string[]): Promise<boolean> {
    const requestedForUserId = currentUserId()
    const res = await api.post<SeatReviewApiResponse>(
      '/v1/client/seat-review/remove', { employee_ids: employeeIds })
    return applyRemoveResponse(res.data, requestedForUserId)
  }

  return { loaded, gated, state, ensureLoaded, refresh, submitRemoval }
})
